import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit, inject, output, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, ButtonModule, RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  readonly sidebarCollapsed = input(false);
  readonly menuToggle = output<void>();
  readonly mobileMenuToggle = output<void>();
  readonly logout = output<void>();
  protected readonly currentDateTimeText = signal('');
  protected readonly currentUserName = signal('Admin');
  protected readonly userInitials = signal('AM');
  private clock?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    const currentUserName = this.authService.getUserName() || 'Admin';
    this.currentUserName.set(currentUserName);
    this.userInitials.set(this.buildInitials(currentUserName));
    this.updateClock();
    this.ngZone.runOutsideAngular(() => {
      this.clock = setInterval(() => {
        const nextText = this.buildClockText();
        this.ngZone.run(() => this.currentDateTimeText.set(nextText));
      }, 1000);
    });
  }
  ngOnDestroy(): void { if (this.clock) clearInterval(this.clock); }

  private updateClock(): void {
    this.currentDateTimeText.set(this.buildClockText());
  }

  private buildClockText(): string {
    return new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  private buildInitials(userName: string): string {
    const cleaned = userName.trim();
    if (!cleaned) return 'AD';

    const parts = cleaned
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return cleaned.slice(0, 2).toUpperCase();
  }
}
